import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import useOnClickOutside from 'hooks/useOutsideClick';

import { LoginTypes } from 'store/authStore/types';
import useAccountRestoration from 'store/authStore/useAccountRestoration';
import useAuthStore from 'store/authStore/useAuthStore';

import { ModalService } from 'services/globalServices';

import ArrowDown from 'assets/icons/arrows/arrow-down-16.svg';
import CloseIcon from 'assets/icons/nav/icon-close-alt-16.svg';
import MobileMenuIcon from 'assets/icons/nav/icon-mobile-menu-16.svg';

import Identicon from 'basics/Identicon';

import ChooseLoginMethodModal from 'modals/auth/ChooseLoginMethodModal';

import {
    Wrapper,
    AccountBlockWeb,
    AccountBlockMobile,
    IconsBlock,
    AppIcon,
    AccountAddresses,
    Arrow,
    AccountFederation,
    AccountPublic,
    SignInButton,
    MobileMenu,
    MobileMenuButton,
    CloseMenuButton,
    BodyStyle,
} from './AccountBlock.styled';
import { isHeaderAccount, readHeaderCache, writeHeaderCache } from './Header.cache';

import AppMenu from '../AppMenu';

const AccountBlock = ({ navLinks }: { navLinks?: React.ReactNode }): React.ReactNode => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const {
        account,
        federationAddress,
        isFederationPending,
        loginType,
        metadata,
        isLogged,
        isLoginPending,
    } = useAuthStore();
    const menuRef = useRef(null);
    useOnClickOutside(menuRef, () => setIsMenuOpen(false));

    const signIn = () => {
        ModalService.openModal(ChooseLoginMethodModal, {});
    };

    const toggleMenu = () => {
        setIsMenuOpen(prevState => !prevState);
    };

    const { savedAccount, isRestoring } = useAccountRestoration();
    const [cachedIdentity, setCachedIdentity] = useState(() =>
        readHeaderCache('account', isHeaderAccount),
    );
    const resolvingFederationFor = useRef<string | null>(null);
    const accountId = account?.accountId() ?? savedAccount?.pubKey;
    const federation =
        cachedIdentity && cachedIdentity.pubKey === accountId ? cachedIdentity.federation : '';
    const accountIdView = accountId ? `${accountId.slice(0, 8)}...${accountId.slice(-8)}` : '';

    useEffect(() => {
        if (!isLogged || !account) return;
        if (isFederationPending) {
            resolvingFederationFor.current = accountId;
            return;
        }
        // A previous account's federation value may still be in the auth store.
        if (account.home_domain && resolvingFederationFor.current !== accountId) return;
        resolvingFederationFor.current = null;
        const nextFederation = account.home_domain ? federationAddress : '';
        if (cachedIdentity?.pubKey === accountId && cachedIdentity.federation === nextFederation)
            return;
        const identity = { pubKey: accountId, federation: nextFederation };
        writeHeaderCache('account', identity);
        setCachedIdentity(identity);
    }, [account, accountId, federationAddress, isFederationPending, isLogged, cachedIdentity]);

    return (
        <Wrapper ref={menuRef} data-testid="header-account">
            {accountId ? (
                <AccountBlockWeb
                    as="button"
                    type="button"
                    disabled={!isLogged || isRestoring}
                    aria-label={isRestoring ? 'Restoring account' : 'Account menu'}
                    aria-busy={isRestoring}
                    onClick={toggleMenu}
                    $isMenuOpen={isMenuOpen}
                >
                    <IconsBlock>
                        <Identicon pubKey={accountId} />
                        {loginType === LoginTypes.walletConnect && (
                            <AppIcon src={metadata?.icons?.[0]} alt={metadata?.name} />
                        )}
                    </IconsBlock>

                    <AccountAddresses>
                        {federation && <AccountFederation>{federation}</AccountFederation>}
                        <AccountPublic>{accountIdView}</AccountPublic>
                    </AccountAddresses>

                    <Arrow $isMenuOpen={isMenuOpen}>
                        <ArrowDown />
                    </Arrow>
                </AccountBlockWeb>
            ) : (
                <SignInButton onClick={signIn} pending={isLoginPending || isRestoring}>
                    sign in
                </SignInButton>
            )}

            <AccountBlockMobile>
                <MobileMenu onClick={() => toggleMenu()}>
                    {isMenuOpen ? (
                        <CloseMenuButton>
                            <CloseIcon />
                            <BodyStyle />
                        </CloseMenuButton>
                    ) : (
                        <MobileMenuButton>
                            <MobileMenuIcon />
                            Menu
                        </MobileMenuButton>
                    )}
                </MobileMenu>
            </AccountBlockMobile>
            {isMenuOpen && <AppMenu navLinks={navLinks} closeMenu={() => setIsMenuOpen(false)} />}
        </Wrapper>
    );
};

export default AccountBlock;
